import { useNavigate, useLocation } from 'react-router';
import { Icon } from '@/kit/Icon';
import { Tooltip } from '@/kit/Tooltip';
import { isLinkActive } from '@/app/layouts/AsideMenu';
import type { ReactNode } from 'react';
import './SideMenu.scss';

export interface SideMenuItem {
  to: string;
  icon: string;
  label: string;
  /** Other URLs on which this item stays highlighted (a screen reached from it, or a group's other screens). */
  alsoActiveFor?: string[];
  /** A short one-line description, shown as a hover tooltip on an info icon while this item is the active tab (`SectionNav` only). */
  infoText?: string;
}

interface SideMenuProps {
  items: SideMenuItem[];
  /** Angular `closeMenu`: true is the initial, collapsed-toggle state. */
  closeMenu: boolean;
  onToggle: () => void;
  /** Rendered between/after the links (the settings menu's Report Settings group). */
  children?: ReactNode;
}

/** An item is active on its own URL and on the URLs it lists in `alsoActiveFor` (APT346, APT348). */
export const isItemActive = (pathname: string, item: SideMenuItem): boolean =>
  isLinkActive(pathname, item.to) || (item.alsoActiveFor?.some((to) => isLinkActive(pathname, to)) ?? false);

interface SubMenuLinkProps {
  to: string;
  icon: string;
  label: string;
  active: boolean;
  /** The menu is collapsed to its icon strip: the row shows its icon only and the Tooltip names it. */
  collapsed: boolean;
}

/**
 * One row of a left menu (every menu, including the Settings one): the icon and the label. Collapsed to the icon strip the label is hidden
 * and the row is named by a Tooltip on hover and on keyboard focus (design review 2 Nov: one menu that collapses to icons with tooltips).
 * The row is a keyboard link: Tab reaches it, Enter or Space opens it.
 */
export function SubMenuLink({ to, icon, label, active, collapsed }: SubMenuLinkProps) {
  const navigate = useNavigate();
  const open = () => void navigate(to);
  return (
    <Tooltip title={collapsed ? label : null} placement="right">
      <li
        role="link"
        tabIndex={0}
        className={`sub-menu-item ${active ? 'active' : ''}`.trim()}
        onClick={open}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            open();
          }
        }}
        data-testid={label}
        aria-label={label}
      >
        {icon && <Icon name={icon} outlined />}
        <span className="sub-menu-label">{label}</span>
      </li>
    </Tooltip>
  );
}

interface ExternalSubMenuLinkProps {
  /** The page to open in a new tab; null shows the row disabled (with `disabledReason` as its tooltip). */
  href: string | null;
  icon: string;
  label: string;
  disabledReason?: string;
  collapsed: boolean;
}

/**
 * A row that leaves the app (APT363: "View on Companies House"): a real link that opens in a new tab, so it is never the highlighted
 * row. It looks like the other rows; the link covers the whole row. With no address to open it is a disabled button that says why.
 */
export function ExternalSubMenuLink({ href, icon, label, disabledReason, collapsed }: ExternalSubMenuLinkProps) {
  const content = (
    <>
      {icon && <Icon name={icon} outlined />}
      <span className="sub-menu-label">{label}</span>
    </>
  );
  return (
    <Tooltip title={collapsed ? label : null} placement="right">
      <li className="sub-menu-item sub-menu-item--external" data-testid={label} title={href ? undefined : disabledReason}>
        {href ? (
          <a className="sub-menu-anchor" href={href} target="_blank" rel="noopener noreferrer" aria-label={`${label} (opens in a new tab)`}>
            {content}
          </a>
        ) : (
          <button type="button" className="sub-menu-anchor" disabled aria-label={label}>
            {content}
          </button>
        )}
      </li>
    </Tooltip>
  );
}

/**
 * The left side list of every per-client area (Angular app-task-menu, app-settings-menu, ...): same DOM and classes,
 * a link list plus the collapse toggle. Clicking a row navigates (Angular put routerLink on the `li`).
 */
export function SideMenu({ items, closeMenu, onToggle, children }: SideMenuProps) {
  const { pathname } = useLocation();

  return (
    <div className="side-menu-host">
      <div className="d-flex h-100">
        <div
          className={`side-list side-list-menu sub-menu ${closeMenu ? '' : 'hide-sidelist'}`.trim()}
          data-testid="sub-menu"
        >
          <div className="side-list-wrapper h-100">
            <ul className="sub-menu-list">
              {children ??
                items.map((item) => (
                  <SubMenuLink
                    key={item.to}
                    to={item.to}
                    icon={item.icon}
                    label={item.label}
                    active={isItemActive(pathname, item)}
                    collapsed={!closeMenu}
                  />
                ))}
            </ul>
          </div>
        </div>
      </div>

      <div
        className={`menu-toggle menu-side-toggle collapse-arrow aside-toggle ${closeMenu ? '' : 'toggle-icon'}`.trim()}
        role="button"
        tabIndex={0}
        aria-label={closeMenu ? 'maximise' : 'minimize'}
        title={closeMenu ? 'maximise' : 'minimize'}
        data-testid={closeMenu ? 'maximise' : 'minimize'}
        onClick={onToggle}
      >
        <Icon name={closeMenu ? 'keyboard_arrow_left' : 'keyboard_arrow_right'} />
        <span className="sr-only" style={{ display: 'none' }}>
          {closeMenu ? 'maximise' : 'minimize'}
        </span>
      </div>
    </div>
  );
}
